import React, { useState } from 'react';
import { Modal, Box, Button, Typography, TextField } from '@mui/material';
import TusFileUpload from '../../../../helper/TusFileUpload';
import apiClient from '../../../../api/apiClient';
import { toast } from 'react-toastify';
import uploadIcon from '/src/assets/images/application/folder-file-up.svg?url';
import { ACTION_BLUE } from '../../../../theme/theme';

interface FileUploadModalProps {
  publisherId: string;
  applicationId: string;
  open: boolean;
  onClose: () => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  publisherId,
  applicationId,
  open,
  onClose,
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [changeLog, setChangeLog] = useState('');

  const handleFileUpload = async (
    selectedFile: File | null,
    selectedFileUrl?: string,
  ) => {
    if (!selectedFile || !selectedFileUrl) {
      toast.error('لطفاً یک فایل انتخاب کنید.');
      return;
    }
    setFile(selectedFile);
    setFileUrl(selectedFileUrl);
  };

  const handleSubmit = async () => {
    if (!file || !fileUrl) {
      toast.error('لطفاً یک فایل انتخاب کنید.');
      return;
    }

    if (!changeLog) {
      toast.error('لطفاً لاگ تغییرات را وارد کنید.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('publisher_id', publisherId);
      formData.append('application_id', applicationId);
      formData.append('change_log', changeLog);
      // Only the tus url. The ipa has already been uploaded in chunks by
      // TusFileUpload and the api downloads it from that url whenever one is
      // present (ApplicationService::resolveIpaPath), so posting the File
      // here as well sent the whole ipa a second time inside this request —
      // and nginx rejects bodies over 51 MB with 413 before php ever sees
      // them, which is exactly the size range a real app build lives in.
      formData.append('application_file_url', fileUrl);
      // لاگ کردن محتویات FormData برای دیباگ
      for (const [key, value] of formData.entries()) {
        console.log(`FormData: ${key} =`, value);
      }

      const response = await apiClient.post(
        '/v2/publisher/applications/versions/create',
        formData,
        {
          headers: {
            Accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.9,fa;q=0.8',
            Origin: 'http://localhost:4000',
            Referer: 'http://localhost:4000/',
            'Sec-Ch-Ua':
              '"Google Chrome";v="137", "Chromium";v="137", "Not/A)Brand";v="24"',
            'Sec-Ch-Ua-Mobile': '?0',
            'Sec-Ch-Ua-Platform': '"macOS"',
            'Sec-Fetch-Dest': 'empty',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Site': 'cross-site',
            'User-Agent':
              'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36',
          },
        },
      );

      toast.success('فایل با موفقیت ارسال شد.');
      console.log('Server response:', response.data);
      onClose();
    } catch (error: any) {
      const errorMessage = error.message || 'خطا در ارسال فایل به سرور.';
      toast.error(errorMessage);
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="file-upload-modal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', md: 300 },
          bgcolor: 'background.paper',
          padding: 40,
          boxShadow: 24,
          p: 8,
          borderRadius: 2,
          direction: 'rtl',
        }}
      >
        <Typography id="file-upload-modal" variant="h6" component="h2" mb={2}>
          آپلود نسخه جدید
        </Typography>
        <TextField
          label="لاگ تغییرات"
          multiline
          rows={4}
          value={changeLog}
          onChange={(e) => {
            setChangeLog(e.target.value);
            console.log('ChangeLog updated:', e.target.value); // لاگ برای دیباگ
          }}
          fullWidth
          sx={{
            mb: 2,
            direction: 'rtl',
            '& .MuiInputLabel-root': {
              right: 30, // جابجایی لیبل به سمت راست
              left: 'auto',
              transformOrigin: 'top right',
              textAlign: 'right',
              // تنظیم موقعیت لیبل در حالت کوچک‌شده (shrink)
              '&.MuiInputLabel-shrink': {
                transform: 'translate(0, -1.5px) scale(0.75)', // تنظیم دقیق موقعیت
                right: 10,
                left: 'auto',
              },
            },
            '& .MuiInputBase-root': {
              direction: 'rtl',
              textAlign: 'right',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              textAlign: 'right', // تراز بردر و notch
              '& legend': {
                textAlign: 'right', // تراز legend برای لیبل
              },
            },
          }}
          required
        />
        <TusFileUpload
          onFilesSelected={handleFileUpload}
          imageSrc={uploadIcon}
          width="100%"
          backgroundColor="#F0F0F0"
          uploadText="فایل .ipa را انتخاب کنید"
          acceptedFileTypes={['.ipa']}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ ml: 1 }}
            disabled={uploading}
          >
            لغو
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: ACTION_BLUE,
              '&:hover': { backgroundColor: '#1565C0' },
            }}
            disabled={uploading || !fileUrl || !changeLog}
          >
            {uploading ? 'در حال ارسال...' : 'ارسال'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FileUploadModal;
